import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';

const services = [
  {
    number: '01',
    title: 'Off-Plan Advisory & Acquisition',
    description: 'Launch access, payment-plan analysis, developer due diligence and allocation strategy for buyers entering the market early.'
  },
  {
    number: '02',
    title: 'Portfolio Management',
    description: 'Ongoing review of holdings, handover timelines, rental positioning and exit opportunities across Dubai assets.'
  },
  {
    number: '03',
    title: 'Investment Structuring',
    description: 'Yield, liquidity and capital-growth thinking for clients balancing lifestyle property with long-term investment objectives.'
  },
  {
    number: '04',
    title: 'Private Client Services',
    description: 'Discreet representation for family offices, founders and institutions seeking privacy through search, negotiation and closure.'
  },
  {
    number: '05',
    title: 'Golden Visa Advisory',
    description: 'Guidance on property-linked UAE residency pathways, qualifying values and coordination with trusted specialist partners.'
  },
  {
    number: '06',
    title: 'Mortgage & Financing',
    description: 'Preferred access to lenders for resident, non-resident and refinancing scenarios with a clean pre-approval pathway.'
  }
];

const processSteps = [
  {
    number: '01',
    title: 'Discovery',
    description: 'We understand capital source, residency needs, desired lifestyle, risk appetite and acquisition timeline.'
  },
  {
    number: '02',
    title: 'Shortlist',
    description: 'We curate a compact list of relevant opportunities, with context on developer quality, market depth and exit strategy.'
  },
  {
    number: '03',
    title: 'Negotiation',
    description: 'We manage offer strategy, document flow, reservation terms and stakeholder coordination with discretion.'
  },
  {
    number: '04',
    title: 'Aftercare',
    description: 'We remain involved through handover, leasing, resale planning, financing or relocation support as required.'
  }
];

const Services = () => {
  useEffect(() => {
    document.body.classList.add('elevate-home', 'inner-page');
    window.scrollTo({ top: 0, behavior: 'auto' });

    const previousTitle = document.title;
    const descriptionElement = document.head.querySelector('meta[name="description"]');
    const previousDescription = descriptionElement?.getAttribute('content') || '';

    document.title = 'Services - Elevate Realty';
    descriptionElement?.setAttribute(
      'content',
      'Elevate Realty services - off-plan advisory, portfolio management, private client services, Golden Visa guidance and mortgage support in Dubai.'
    );

    window.AOS?.init({ duration: 800, once: true, offset: 80 });
    window.AOS?.refreshHard?.();

    return () => {
      document.body.classList.remove('elevate-home', 'inner-page');
      document.title = previousTitle;
      descriptionElement?.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <>
      <Header />

      <main className="inner-main" id="top">
        <section className="inner-hero services-hero">
          <div className="inner-hero__content" data-aos="fade-up">
            <p className="eyebrow">Services</p>
            <h1>Advisory designed around <em>outcomes.</em></h1>
            <p>
              From first acquisition to portfolio stewardship, Elevate brings discipline,
              access and quiet execution to every Dubai real estate decision.
            </p>
            <div className="inner-kicker-row">
              <Link className="primary-link" to="/contact-us">Request advisory</Link>
              <Link className="outline-link" to="/new-projects">View properties</Link>
            </div>
          </div>
        </section>

        <section className="inner-section">
          <div className="inner-section-header">
            <p className="eyebrow">What we do</p>
            <h2>Six ways we help clients move with confidence.</h2>
          </div>
          <div className="service-feature-grid">
            {services.map((service) => (
              <article className="service-feature" key={service.number}>
                <span>{service.number}</span>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="inner-section is-bordered">
          <div className="inner-split">
            <div className="inner-copy" data-aos="fade-up">
              <p className="eyebrow">How we work</p>
              <h2>Measured advice before movement.</h2>
              <p>
                Every engagement starts with a clear brief. We define the role of the asset,
                the risk appetite, the timing and the practical requirements before introducing
                opportunities.
              </p>
              <p>The result is a calmer search, sharper negotiation and fewer distractions.</p>
            </div>
            <div className="inner-media" data-aos="fade-up" data-aos-delay="100">
              <img src="/assets/img/cta-bg-5.jpg" alt="Luxury Dubai residence interior" />
              <span className="inner-media__caption">Private viewings - confidential mandates</span>
            </div>
          </div>
        </section>

        <section className="inner-section">
          <div className="inner-section-header">
            <p className="eyebrow">Process</p>
            <h2>A clear path from brief to completion.</h2>
          </div>
          <div className="process-list">
            {processSteps.map((step) => (
              <article className="process-item" key={step.number}>
                <span>{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <Footer />

      <a
        className="advisor-button"
        href="https://api.whatsapp.com/send?phone=971507135432&text=Hi"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Speak with an advisor on WhatsApp"
      >
        <svg className="advisor-button__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M21 11.5a8.5 8.5 0 0 1-12.7 7.4L3 20l1.2-5A8.5 8.5 0 1 1 21 11.5Z" />
        </svg>
        <span>Speak with an advisor</span>
      </a>
    </>
  );
};

export default Services;
